/**
 * The Home Screen on iOS (decision 3; part 40a, table H). A Home Screen web app keeps its storage apart from the
 * browser's, and Safari may erase the data of a site not visited for 7 days, so in a browser on iOS (Safari, or another
 * built on it), outside the Home Screen app, the first screen asks the writer to add the app to the Home Screen before
 * writing. Writing in the browser anyway is remembered on the device, and a banner names the risk from then on (moving
 * the work is parts 36 and 40).
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';

/** The browser, as far as the rule reads it (the browser's `navigator`). */
export interface DeviceLike {
  readonly userAgent: string;
  readonly maxTouchPoints: number;
  /** Safari's own flag: true in a Home Screen app; absent elsewhere. */
  readonly standalone?: unknown;
}

/** What the writer is shown: nothing (H1, H5), the Home Screen screen (H2), or the workspace with the banner (H3, H4). */
export type HomeScreenState = 'none' | 'ask' | 'warn';

/** H1, H2: whether the browser runs on iOS or iPadOS (which reports a Mac's user agent, with touch). */
export function onIos(device: DeviceLike): boolean {
  return /\b(?:iPhone|iPad|iPod)\b/u.test(device.userAgent) || (/\bMacintosh\b/u.test(device.userAgent) && device.maxTouchPoints > 1);
}

/** H2, H5: whether the page runs on iOS outside the Home Screen app (`standalone` is the display mode's match). */
export function outsideHomeScreen(device: DeviceLike, standalone: boolean): boolean {
  return onIos(device) && device.standalone !== true && !standalone;
}

/** The device's storage for the choice (the browser's `localStorage`), as far as it is used. */
export interface ChoiceStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The key the choice is kept under, and its one value. */
export const CHOICE_KEY = 'cockatrice.home-screen';
export const IN_SAFARI = 'safari';

/** The remembered choice (H3, H4, H6): storage that throws (none, refused, full) remembers nothing. */
export function storedChoice(storage: () => ChoiceStorage): { readonly remembered: () => boolean; readonly remember: () => void } {
  return {
    remembered: () => {
      try {
        return storage().getItem(CHOICE_KEY) === IN_SAFARI;
      } catch {
        return false;
      }
    },
    remember: () => {
      try {
        storage().setItem(CHOICE_KEY, IN_SAFARI);
      } catch {
        // H6: the choice holds for this page only.
      }
    },
  };
}

/** Table H's state for the page. */
export class HomeScreen {
  readonly state: Accessor<HomeScreenState>;
  readonly #setState: (state: HomeScreenState) => void;
  readonly #remember: () => void;

  /** `outside`: H2's test; `choice`: the remembered choice. */
  constructor(outside: boolean, choice: { readonly remembered: () => boolean; readonly remember: () => void }) {
    const [state, setState] = createSignal<HomeScreenState>(outside ? (choice.remembered() ? 'warn' : 'ask') : 'none');
    this.state = state;
    this.#setState = setState;
    this.#remember = choice.remember;
  }

  /** H3: "Keep writing in the browser": remembered, and the workspace with the banner. */
  keepHere(): void {
    if (this.state() !== 'ask') return;
    this.#remember();
    this.#setState('warn');
  }
}
